import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import type { OmitSafely } from '@/types';

import { CustomDropdown, type CustomDropdownProps } from './CustomDropdown';
import FieldErrors from './FieldErrors';

export type DropdownFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  onChange?: (value: string) => void;
  onBeforeChange?: (value: string) => Promise<boolean>;
} & OmitSafely<CustomDropdownProps, 'value' | 'onChange' | 'onBlur'>;

export const DropdownFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  fieldPath,
  control,
  rules,
  onChange,
  onBeforeChange,
  ...rest
}: DropdownFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { onBlur, onChange: innerOnChange, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  const composedOnChange = useCallback(
    async (selectedValue: string) => {
      const canChange = onBeforeChange ? await onBeforeChange(selectedValue) : true;
      if (canChange) {
        innerOnChange(selectedValue);
        onChange?.(selectedValue);
      }
    },
    [onBeforeChange, onChange, innerOnChange],
  );

  return (
    <div>
      <CustomDropdown
        isArrowIcon={true}
        value={value}
        onChange={composedOnChange}
        onBlur={onBlur}
        {...rest}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
